// =====================================================================
// PrelevementsList.jsx - Lista prelevements fournitures (M-001 Faza 2)
// =====================================================================
// Replica modulul Odoo "Prelevements fournitures" (action-969).
//
// Permission gates:
//   - read.all -> vede toate entries (COORDINATOR, SAV, FINANCE, ADMIN)
//   - read.own -> vede doar propriile entries (OPERATOR_CLIENT)
//   - create.own / create.all -> buton "Nouveau" vizibil
//   - update.own + same-day -> click pe rand propriu deschide form edit
//   - update.all -> click pe orice rand deschide form edit
//   - delete.all -> buton "Supprimer" in form edit (ADMIN)
//   - invoice.all -> toggle "Facture" in coloana (FINANCE/ADMIN)
//
// OPERATOR (fara perms supply_draws.*) -> nici sidebar link, nici acces.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { supplyDraws as drawsApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import PrelevementFormModal from './PrelevementFormModal.jsx';

const CATEGORY_LABELS_FR = {
  filmage:   'Filmage',
  carton:    'Carton',
  etiquette: 'Étiquette',
  adhesif:   'Adhésif',
  papier:    'Papier',
  pochette:  'Pochette',
};

const PAGE_SIZE = 80;

export default function PrelevementsList({ embedded = false }) {
  const { user, can } = useAuth();
  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // Filtre
  const [search, setSearch]               = useState('');
  const [filterInvoiced, setFilterInvoiced] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');

  // Modal state
  const [modalOpen, setModalOpen]   = useState(false);
  const [editingId, setEditingId]   = useState(null);

  // Export state
  const [exporting, setExporting]   = useState(null); // 'csv' | 'xlsx' | null

  // Permissions
  const canCreate    = can(['supply_draws.create.own', 'supply_draws.create.all']);
  const canReadAll   = can('supply_draws.read.all');
  const canInvoice   = can('supply_draws.invoice.all');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (search.trim())              params.search = search.trim();
      if (filterInvoiced !== 'all')   params.invoiced = filterInvoiced;
      if (filterCategory !== 'all')   params.category = filterCategory;
      if (filterDateFrom)             params.dateFrom = filterDateFrom;
      if (filterDateTo)               params.dateTo   = filterDateTo;

      const res = await drawsApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('[PrelevementsList] fetch error', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterInvoiced, filterCategory, filterDateFrom, filterDateTo]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [search, filterInvoiced, filterCategory, filterDateFrom, filterDateTo]);

  const handleOpenCreate = () => { setEditingId(null); setModalOpen(true); };
  const handleOpenEdit = (drawId) => { setEditingId(drawId); setModalOpen(true); };
  const handleModalClose = () => { setModalOpen(false); setEditingId(null); };
  const handleModalSaved = () => { setModalOpen(false); setEditingId(null); fetchList(); };

  const handleToggleInvoice = async (item, e) => {
    e.stopPropagation();
    if (!canInvoice) return;
    const confirmMsg = item.invoiced
      ? `Marquer ${item.ref} comme NON facturé ?`
      : `Marquer ${item.ref} comme facturé ?`;
    if (!confirm(confirmMsg)) return;
    try {
      await drawsApi.toggleInvoice(item.id, !item.invoiced);
      fetchList();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du basculement');
    }
  };

  // Export handler (CSV sau XLSX) - foloseste filtre curente
  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format });
      if (search.trim())            params.append('q', search.trim());
      if (filterInvoiced !== 'all') params.append('invoiced', filterInvoiced);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterDateFrom)           params.append('dateFrom', filterDateFrom);
      if (filterDateTo)             params.append('dateTo', filterDateTo);

      const response = await fetch(`/api/supply-draws/export?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errData.message || 'Erreur lors de l\'export');
      }

      // Extract filename from Content-Disposition or fallback
      const cd = response.headers.get('Content-Disposition') || '';
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : `prelevements.${format}`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[PrelevementsList] export error', err);
      alert(err.message || 'Erreur lors de l\'export');
    } finally {
      setExporting(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstIdx   = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastIdx    = Math.min(page * PAGE_SIZE, total);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div style={{ padding: embedded ? 0 : 16, fontSize: 13 }}>
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>Prélèvements fournitures</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
              {canReadAll
                ? 'Tous les prélèvements de matériaux d\'emballage.'
                : 'Vos prélèvements de matériaux d\'emballage.'}
            </p>
          </div>
          {canCreate && (
            <button
              onClick={handleOpenCreate}
              style={{
                padding: '8px 16px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              + Nouveau
            </button>
          )}
        </div>
      )}

      {/* Embedded: just the action button at top-right */}
      {embedded && canCreate && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            onClick={handleOpenCreate}
            style={{
              padding: '8px 16px',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            + Nouveau
          </button>
        </div>
      )}

      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        padding: 12, background: '#f8fafc',
        border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 12,
      }}>
        <input
          type="text"
          placeholder="Rechercher (projet, type, référence...)"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 220px', padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
        />
        <select value={filterInvoiced} onChange={e => setFilterInvoiced(e.target.value)}
                style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
          <option value="all">Tous (facturé)</option>
          <option value="false">Non facturé</option>
          <option value="true">Facturé</option>
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
          <option value="all">Toutes catégories</option>
          {Object.entries(CATEGORY_LABELS_FR).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <label style={{ fontSize: 12, color: '#64748b' }}>Du</label>
        <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
               style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
        <label style={{ fontSize: 12, color: '#64748b' }}>Au</label>
        <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
               style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
        {(search || filterInvoiced !== 'all' || filterCategory !== 'all' || filterDateFrom || filterDateTo) && (
          <button onClick={() => {
            setSearch(''); setFilterInvoiced('all'); setFilterCategory('all');
            setFilterDateFrom(''); setFilterDateTo('');
          }} style={{
            padding: '6px 10px', background: 'white', border: '1px solid #d1d5db',
            borderRadius: 4, fontSize: 12, cursor: 'pointer',
          }}>Effacer</button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {loading ? 'Chargement...' : `${firstIdx}-${lastIdx} / ${total}`}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('csv', exporting === 'csv')}
            title="Exporter en CSV (UTF-8 BOM, compatible Excel)"
          >
            {exporting === 'csv' ? '⏳ Export…' : `📥 Exporter CSV (${total})`}
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('xlsx', exporting === 'xlsx')}
            title="Exporter en XLSX avec mise en forme"
          >
            {exporting === 'xlsx' ? '⏳ Export…' : '📊 Exporter XLSX'}
          </button>
          <span style={{ width: 12 }} />
          <button onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading} style={paginationBtnStyle(page <= 1)}>◀</button>
          <span style={{ fontSize: 12, padding: '0 8px' }}>Page {page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading} style={paginationBtnStyle(page >= totalPages)}>▶</button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: 12, background: '#fee2e2', color: '#991b1b',
          border: '1px solid #fca5a5', borderRadius: 4, marginBottom: 12,
        }}>{error}</div>
      )}

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: 'white' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Projet</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Quantité</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Catégorie</th>
              <th style={thStyle}>Créé par</th>
              <th style={thStyle}>Référence</th>
              {canInvoice && <th style={{ ...thStyle, textAlign: 'center' }}>Facturé</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canInvoice ? 8 : 7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Chargement...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={canInvoice ? 8 : 7} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                Aucun prélèvement trouvé.
              </td></tr>
            ) : items.map(item => (
              <tr key={item.id}
                  onClick={() => handleOpenEdit(item.id)}
                  style={{
                    borderBottom: '1px solid #e2e8f0', cursor: 'pointer',
                    background: item.invoiced ? '#f0fdf4' : 'white',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = item.invoiced ? '#dcfce7' : '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = item.invoiced ? '#f0fdf4' : 'white'}>
                <td style={tdStyle}>{formatDate(item.date)}</td>
                <td style={tdStyle}>{item.project?.name || '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {item.quantity}
                </td>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 500 }}>{item.supplyType?.code}</span>
                  <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>
                    {item.supplyType?.label}
                  </span>
                </td>
                <td style={tdStyle}>
                  {item.supplyType?.category
                    ? <span style={categoryBadgeStyle(item.supplyType.category)}>
                        {CATEGORY_LABELS_FR[item.supplyType.category] || item.supplyType.category}
                      </span>
                    : '—'}
                </td>
                <td style={tdStyle}>{item.createdBy?.name || '—'}</td>
                <td style={{ ...tdStyle, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                  {item.ref}
                </td>
                {canInvoice && (
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button onClick={e => handleToggleInvoice(item, e)}
                            title={item.invoiced ? 'Marquer comme NON facturé' : 'Marquer comme facturé'}
                            style={{
                              padding: '2px 8px', fontSize: 11, fontWeight: 500,
                              border: '1px solid',
                              borderColor: item.invoiced ? '#86efac' : '#cbd5e1',
                              background: item.invoiced ? '#dcfce7' : 'white',
                              color: item.invoiced ? '#166534' : '#64748b',
                              borderRadius: 3, cursor: 'pointer',
                            }}>
                      {item.invoiced ? '✓ Facturé' : 'Non facturé'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <PrelevementFormModal
          drawId={editingId}
          onClose={handleModalClose}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}

const thStyle = {
  padding: '10px 12px', textAlign: 'left', fontWeight: 500, fontSize: 12,
  color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3,
};
const tdStyle = { padding: '10px 12px' };
const paginationBtnStyle = (disabled) => ({
  padding: '4px 10px', background: disabled ? '#f1f5f9' : 'white',
  border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? '#94a3b8' : '#1e293b',
});

const exportBtnStyle = (format, loading) => ({
  padding: '4px 10px',
  background: loading ? '#f1f5f9' : (format === 'xlsx' ? '#16a34a' : '#3b82f6'),
  color: loading ? '#94a3b8' : 'white',
  border: '1px solid ' + (loading ? '#cbd5e1' : (format === 'xlsx' ? '#15803d' : '#2563eb')),
  borderRadius: 4,
  fontSize: 12,
  fontWeight: 500,
  cursor: loading ? 'wait' : 'pointer',
});

const CATEGORY_COLORS = {
  filmage:   { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd' },
  carton:    { bg: '#fef3c7', fg: '#78350f', border: '#fbbf24' },
  etiquette: { bg: '#fce7f3', fg: '#831843', border: '#f472b6' },
  adhesif:   { bg: '#fed7aa', fg: '#7c2d12', border: '#fb923c' },
  papier:    { bg: '#e0e7ff', fg: '#3730a3', border: '#818cf8' },
  pochette:  { bg: '#d1fae5', fg: '#065f46', border: '#34d399' },
};
const categoryBadgeStyle = (cat) => {
  const c = CATEGORY_COLORS[cat] || { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' };
  return {
    display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
    background: c.bg, color: c.fg, border: `1px solid ${c.border}`, borderRadius: 3,
  };
};
